import React, { useState, useEffect, useCallback, useRef, lazy } from 'react';
import { Lazy } from '../components/Boundary.jsx';
import { supabase, fetchAllRows } from '../lib/supabase.js';
import { heuresOuvrees, DELAI_ALERTE_H } from '../lib/tickets.js';
import useRealtimeSessions from '../lib/useRealtimeSessions.js';
import OdooSync from '../components/OdooSync.jsx';
import PrepDetail from '../components/PrepDetail.jsx';
import QuickPanel from '../components/QuickPanel.jsx';
import OfflineBanner from '../components/OfflineBanner.jsx';
import { TOURNEE_COLORS, tourneeSortKey } from '../lib/constants.js';
import { DEMO_SESSIONS, DEMO_PRODUCTS } from '../lib/demo.js';
import ModuleTutorial, { resetModuleTutorial, resetAllTutorials } from '../components/ModuleTutorial.jsx';
import { TUTO_GLOBAL, TUTO_PREP, TUTO_RECEPTION_T, TUTO_RECEPTION_P } from '../lib/tutorial-content.jsx';
import PullToRefresh from '../components/PullToRefresh.jsx';

// Lazy-loaded modules (chargés à la demande)
const DepartsTab = lazy(() => import('../components/DepartsTab.jsx'));
const AnomaliesTab = lazy(() => import('../components/AnomaliesTab.jsx'));
const CaissesTab = lazy(() => import('../components/CaissesTab.jsx'));
const EquipeTab = lazy(() => import('../components/EquipeTab.jsx'));
const ControlePrepTab = lazy(() => import('../components/ControleTab.jsx'));
const ReliquatsTab = lazy(() => import('../components/ReliquatsTab.jsx'));
const HistoriqueTab = lazy(() => import('../components/HistoriqueTab.jsx'));
const ReclamationsTab = lazy(() => import('../components/ReclamationsTab.jsx'));
const TicketsTab = lazy(() => import('../components/TicketsTab.jsx'));
const StockCasTab = lazy(() => import('../components/StockCasTab.jsx'));
const ImpressionFiches = lazy(() => import('../components/ImpressionFiches.jsx'));
const DerogationsTab = lazy(() => import('../components/DerogationsTab.jsx'));
const LivraisonSamediTab = lazy(() => import('../components/LivraisonSamediTab.jsx'));
const LitigesExportTab = lazy(() => import('../components/LitigesExportTab.jsx'));
const SuiviErreursTab = lazy(() => import('../components/SuiviErreursTab.jsx'));
const CorrectionsReceptionTab = lazy(() => import('../components/CorrectionsReceptionTab.jsx'));
const StatsTab = lazy(() => import('../components/StatsTab.jsx'));
const StupsTab = lazy(() => import('../components/StupsTab.jsx'));
const ReceptionTransporteurs = lazy(() => import('../components/ReceptionTransporteurs.jsx'));
const ReceptionProduits = lazy(() => import('../components/ReceptionProduits.jsx'));
const ExportPrep = lazy(() => import('../components/ExportPrep.jsx'));
const ReceptionV2App = lazy(() => import('../components-v2/ReceptionV2App.jsx'));
const PrepV2App = lazy(() => import('../components-prep-v2/PrepV2App.jsx'));

export default function Home({ user, onLogout }) {
  // Restaurer l'état depuis sessionStorage (survie au switch app TSL ↔ Elixir Prep)
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem('elixir_nav') || '{}'); } catch { return {}; } })();

  const [module, setModule] = useState(() => {
    if (window.location.hash === '#reception-v2') return 'reception_v2';
    return saved.module || null;
  });

  // Détection hash #reception-v2 après login
  useEffect(() => {
    if (window.location.hash === '#reception-v2' && module !== 'reception_v2') {
      setModule('reception_v2');
    }
  }, []);
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(saved.tab || 'pharma');
  const [odooOpen, setOdooOpen] = useState(false);
  const [collapsedT, setCollapsedT] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedInitialStep, setSelectedInitialStep] = useState(null);
  const [pendingReliquats, setPendingReliquats] = useState({});
  const [reclamCount, setReclamCount] = useState(0);
  const [ticketsCount, setTicketsCount] = useState(0);
  const [ticketsRetard, setTicketsRetard] = useState(0);
  const [casStockCount, setCasStockCount] = useState(0);
  const [sensiblesCount, setSensiblesCount] = useState(0);
  const [visaCount, setVisaCount] = useState(0);
  const [correctionsCount, setCorrectionsCount] = useState(0);
  const [stupsCount, setStupsCount] = useState(0);
  const [visaOldestDays, setVisaOldestDays] = useState(0);
  const [derogCount, setDerogCount] = useState(0);
  const [samediCount, setSamediCount] = useState(0);
  const [totalLines, setTotalLines] = useState(0);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [tutoKey, setTutoKey] = useState(0);
  const [prepTutoKey, setPrepTutoKey] = useState(0);
  const [quickPanelSession, setQuickPanelSession] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [showAddPrepModal, setShowAddPrepModal] = useState(false);
  const savedSessionId = useRef(saved.sessionId || null);

  // Persister l'état de navigation pour survie switch app (TSL ↔ Elixir Prep)
  useEffect(() => {
    try { sessionStorage.setItem('elixir_nav', JSON.stringify({ module, tab, sessionId: selectedSession?.id || null })); } catch {}
  }, [module, tab, selectedSession?.id]);

  const isDemo = user?.isDemo === true;

  // ═══ GESTION HISTORIQUE NAVIGATEUR — back button physique ═══
  const navRef = React.useRef({ module: null, tab: 'pharma', detail: false, panel: false });
  const skipPopRef = React.useRef(false);

  function navPush(state) {
    navRef.current = state;
    window.history.pushState(state, '');
  }

  // Quand on entre dans un module
  function navigateToModule(mod) {
    navPush({ module: mod, tab: 'pharma', detail: false, panel: false });
    setModule(mod);
  }

  // Quand on change de tab dans le module prep
  function navigateToTab(t) {
    navPush({ module: navRef.current?.module || module, tab: t, detail: false, panel: false });
    setTab(t);
    setSelectedSession(null);
    setQuickPanelSession(null);
  }

  // Quand on ouvre le QuickPanel
  function openQuickPanel(s) {
    navPush({ ...navRef.current, panel: true, detail: false });
    setQuickPanelSession(s);
  }

  // Quand on ferme le QuickPanel manuellement (pas via back)
  function closeQuickPanel() {
    setQuickPanelSession(null);
    if (navRef.current?.panel) {
      skipPopRef.current = true;
      window.history.back();
    }
  }

  // Quand on ouvre une fiche détaillée
  function openDetail(s, step) {
    navPush({ ...navRef.current, detail: true, panel: false });
    setSelectedSession(s);
    if (step) setSelectedInitialStep(step);
  }

  // Quand on ferme la fiche manuellement (pas via back)
  function closeDetail() {
    setSelectedSession(null);
    setSelectedInitialStep(null);
    if (navRef.current?.detail) {
      skipPopRef.current = true;
      window.history.back();
    }
  }

  // Quand on quitte un module manuellement
  function leaveModule() {
    moduleUnsavedRef.current = false;
    setModule(null);
    setTab('pharma');
    setSelectedSession(null);
    setQuickPanelSession(null);
    try { sessionStorage.removeItem('elixir_nav'); } catch {}
    // Revenir à la base de l'historique
    if (navRef.current?.module) {
      skipPopRef.current = true;
      window.history.back();
    }
    navRef.current = { module: null, tab: 'pharma', detail: false, panel: false };
  }

  // Écouter le back du téléphone
  useEffect(() => {
    function handlePopState(e) {
      if (skipPopRef.current) { skipPopRef.current = false; return; }
      // Flag global posé par CameraCapture quand elle ferme
      if (window.__skipNextPopState) { window.__skipNextPopState = false; return; }
      // Ne pas interférer avec la caméra (elle gère son propre popstate)
      if (e.state?.camera) return;
      // Caméra ouverte → ignorer complètement
      if (window.__cameraOpen) return;

      // Fermer le QuickPanel
      if (quickPanelSession) { setQuickPanelSession(null); return; }
      // Fermer la fiche détaillée
      if (selectedSession) { setSelectedSession(null); setSelectedInitialStep(null); return; }
      // Retour au tab par défaut (prep module)
      if (tab !== 'pharma' && module === 'prep') { setTab('pharma'); return; }
      // Retour au hub
      if (module) {
        moduleUnsavedRef.current = false;
        setModule(null);
        setTab('pharma');
        navRef.current = { module: null, tab: 'pharma', detail: false, panel: false };
        return;
      }
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [module, tab, selectedSession, quickPanelSession]);

  // Pousser un state quand on ouvre une fiche ou un panel
  useEffect(() => {
    if (selectedSession && !navRef.current.detail) {
      navPush({ ...navRef.current, detail: true });
    }
  }, [selectedSession]);

  useEffect(() => {
    if (quickPanelSession && !navRef.current.panel) {
      navPush({ ...navRef.current, panel: true });
    }
  }, [quickPanelSession]);

  // Garde de sortie pour modules avec données non sauvegardées
  const moduleUnsavedRef = React.useRef(false);
  function guardedLeave() {
    if (moduleUnsavedRef.current) {
      if (!confirm('⚠️ Des données non sauvegardées seront perdues.\nQuitter quand même ?')) return;
    }
    leaveModule();
  }

  // Sync quickPanelSession avec les sessions (realtime updates)
  useEffect(() => {
    if (!quickPanelSession) return;
    const updated = sessions.find(s => s.id === quickPanelSession.id);
    if (updated && updated.updated_at !== quickPanelSession.updated_at) {
      setQuickPanelSession(prev => ({ ...prev, ...updated }));
    }
  }, [sessions]);

  // ═══ REALTIME — callbacks définis ici, hook appelé après loadTodaySessions ═══
  const handleSessionChange = useCallback((type, session) => {
    if (!session) return;
    setSessions(prev => {
      if (type === 'INSERT') {
        if (prev.find(s => s.id === session.id)) return prev;
        if (session.status === 'facturee' || session.status === 'cloturee') return prev;
        return [...prev, session];
      }
      if (type === 'UPDATE') {
        const exists = prev.find(s => s.id === session.id);
        if (!exists) {
          if (session.status === 'facturee' || session.status === 'cloturee') return prev;
          return [...prev, session];
        }
        if (session.status === 'facturee' || session.status === 'cloturee') {
          return prev.filter(s => s.id !== session.id);
        }
        return prev.map(s => s.id === session.id ? { ...s, ...session } : s);
      }
      if (type === 'DELETE') {
        return prev.filter(s => s.id !== session.id);
      }
      return prev;
    });

    // Mettre à jour la session ouverte si modifiée par quelqu'un d'autre
    // MAIS pas si seul updated_at a changé (évite de casser les inputs en cours de saisie)
    setSelectedSession(prev => {
      if (!prev || prev.id !== session.id) return prev;
      if (type === 'DELETE') return null;
      if (type === 'UPDATE') {
        // Ne mettre à jour que si un champ significatif a changé
        const meaningful = ['status', 'prepared_by', 'prepared_by_name', 'nb_amb', 'nb_froid', 'nb_cartons', 'nb_cartons_vrac', 'order_refs', 'tournee'];
        const changed = meaningful.some(k => JSON.stringify(prev[k]) !== JSON.stringify(session[k]));
        if (!changed) return prev;
        return { ...prev, ...session };
      }
      return prev;
    });
  }, []);

  const loadTodaySessions = useCallback(async () => {
    // ═══ MODE DÉMO — données fictives ═══
    if (isDemo) {
      setSessions(DEMO_SESSIONS);
      setTotalLines(DEMO_SESSIONS.reduce((s, d) => s + (DEMO_PRODUCTS[d.id]?.length || 0), 0));
      setPendingReliquats({});
      setLoading(false);
      return;
    }

    try {
      // Charger TOUTES les sessions actives — le worker est responsable du nettoyage
      // L'app est un miroir d'Odoo
      // Lecture paginée : au-delà de 1 000 sessions actives, l'ancien
      // .limit(5000) était plafonné à 1 000 → pharmacies invisibles.
      const [sessionsRows, orderRows] = await Promise.all([
        fetchAllRows(() => supabase.from('elixir_prep_sessions').select('*')
          .not('status', 'in', '("facturee","cloturee")')
          .order('partner_name').order('id')),
        fetchAllRows(() => supabase.from('elixir_prep_pharmacy_order').select('tournee, partner_id, position').order('tournee').order('position')),
      ]);
      const sessionsRes = { data: sessionsRows, error: null };
      const orderRes = { data: orderRows, error: null };
      let filtered = sessionsRes.data || [];

      // Appliquer l'ordre sauvegardé aux sessions sans delivery_position
      const savedOrder = {};
      (orderRes.data || []).forEach(o => { savedOrder[o.tournee + '_' + o.partner_id] = o.position; });
      
      let needsUpdate = false;
      filtered = filtered.map(s => {
        if (!s.delivery_position) {
          const key = (s.tournee || '') + '_' + s.partner_id;
          if (savedOrder[key]) {
            needsUpdate = true;
            return { ...s, delivery_position: savedOrder[key] };
          }
        }
        return s;
      });

      // Sauvegarder les positions appliquées (fire-and-forget)
      if (needsUpdate) {
        filtered.filter(s => s.delivery_position).forEach(s => {
          supabase.from('elixir_prep_sessions').update({ delivery_position: s.delivery_position }).eq('id', s.id).then(() => {});
        });
      }

      // Note : on ne fait PLUS de placeholders Odoo en local (causait erreur
      // 'odoo_placeholder_XXXX' au clic). A la place, le useEffect au montage
      // declenche /.netlify/functions/odoo-sync-trigger qui force le worker
      // a tourner et ecrire dans elixir_prep_sessions. Les nouvelles commandes
      // apparaissent au prochain reload (12s ou 30s plus tard, vu le setTimeout
      // du useEffect initial).

      setSessions(filtered);

      // ═══ LIBÉRATION APRÈS 1 H SANS ACTIVITÉ ═══
      // Une pharmacie prise et laissée de côté bloque tout le monde : personne
      // d'autre ne peut la préparer. Au bout d'une heure sans aucun geste, elle
      // repart dans le pot commun — le travail déjà fait (caisses, photos) est
      // conservé, seule la prise en main est relâchée.
      //
      // L'activité ne se mesure PAS sur updated_at : la synchronisation Odoo le
      // modifie aussi, ce qui ferait passer pour actif quelqu'un d'absent. On
      // regarde les gestes réels : prise en main, caisse scannée, photo.
      try {
        const prises = filtered.filter(s => s.status === 'en_cours' && s.prepared_by);
        if (prises.length > 0) {
          const limite = Date.now() - 60 * 60 * 1000;
          const ids = prises.map(s => s.id);
          const [cRes, phRes] = await Promise.all([
            supabase.from('elixir_prep_caisses').select('session_id, scanned_at').in('session_id', ids),
            supabase.from('elixir_prep_photos').select('session_id, uploaded_at').in('session_id', ids),
          ]);
          const dernier = {};
          const noter = (sid, t) => { const v = t ? new Date(t).getTime() : 0; if (v > (dernier[sid] || 0)) dernier[sid] = v; };
          (cRes.data || []).forEach(c => noter(c.session_id, c.scanned_at));
          (phRes.data || []).forEach(p => noter(p.session_id, p.uploaded_at));
          const aLiberer = prises.filter(s => {
            if (s.locked_at) return false;                  // préparation verrouillée : on n'y touche pas
            // Repli sur updated_at tant que claimed_at n'est pas renseigné
            // (anciennes fiches) : prudent, on relâche moins plutôt que trop.
            noter(s.id, s.claimed_at || s.updated_at);
            return (dernier[s.id] || 0) < limite;
          });
          for (const s of aLiberer) {
            console.warn('Inactif > 1 h —', s.partner_name, 'libérée (était prise par', s.prepared_by_name + ')');
            await supabase.from('elixir_prep_sessions').update({
              prepared_by: null, updated_at: new Date().toISOString(),
            }).eq('id', s.id).eq('prepared_by', s.prepared_by);
          }
          if (aLiberer.length > 0) {
            const libIds = new Set(aLiberer.map(s => s.id));
            filtered = filtered.map(s => libIds.has(s.id) ? { ...s, prepared_by: null } : s);
            setSessions(filtered);
          }
        }
      } catch (e) { console.warn('Libération auto :', e?.message); }

      // ═══ NETTOYAGE DOUBLONS — 1 prépa max par magasinier ═══
      const enCours = filtered.filter(s => s.status === 'en_cours' && s.prepared_by);
      const byUser = {};
      enCours.forEach(s => {
        if (!byUser[s.prepared_by]) byUser[s.prepared_by] = [];
        byUser[s.prepared_by].push(s);
      });
      const releaseIds = new Set();
      for (const [uid, userSessions] of Object.entries(byUser)) {
        if (userSessions.length <= 1) continue;
        userSessions.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
        const toRelease = userSessions.slice(1);
        console.warn('Doublon détecté pour', userSessions[0].prepared_by_name, ':', toRelease.map(s => s.partner_name).join(', '), '→ libéré');
        for (const s of toRelease) {
          await supabase.from('elixir_prep_sessions').update({
            prepared_by: null, updated_at: new Date().toISOString(),
          }).eq('id', s.id);
          releaseIds.add(s.id);
        }
      }
      if (releaseIds.size > 0) {
        filtered = filtered.map(s => releaseIds.has(s.id) ? { ...s, prepared_by: null } : s);
      }
      setSessions(filtered);

      // Count order lines (dépend des sessions, mais en arrière-plan)
      const sessionIds = filtered.map(s => s.id);
      if (sessionIds.length > 0) {
        supabase.from('elixir_prep_order_lines').select('id', { count: 'exact', head: true }).in('session_id', sessionIds)
          .then(({ count }) => setTotalLines(count || 0));
      } else { setTotalLines(0); }

      // Reliquats gérés par le worker Odoo (pickings backorder) — plus de table Supabase
      setPendingReliquats({});
    } catch (e) {
      console.error('Erreur chargement sessions:', e);
    } finally {
      setLoading(false);
    }
  }, [isDemo]);

  useEffect(() => { loadTodaySessions(); }, [loadTodaySessions]);

  // Au montage : declencher un sync Odoo en arriere-plan pour recuperer les
  // commandes recentes qui ne sont pas encore dans Supabase. Sinon V1 ne voit
  // que ce que le worker programme (toutes les 5 min) a deja sync.
  // Cela aligne V1 sur V2 qui lit Odoo directement a chaque ouverture.
  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/.netlify/functions/odoo-sync-trigger', { method: 'POST' });
        if (!res.ok) return;
        // Le worker tourne en background ~10-20s. On recharge apres delai puis a 30s.
        setTimeout(() => { if (!cancelled) loadTodaySessions(); }, 12000);
        setTimeout(() => { if (!cancelled) loadTodaySessions(); }, 30000);
      } catch { /* silencieux : si le sync echoue, on garde les donnees Supabase actuelles */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restaurer la session ouverte après le chargement (survie switch app)
  useEffect(() => {
    if (!loading && sessions.length > 0 && savedSessionId.current && !selectedSession) {
      const found = sessions.find(s => s.id === savedSessionId.current);
      if (found) {
        setSelectedSession(found);
        console.log('🔄 Session restaurée après switch app:', found.partner_name);
      }
      savedSessionId.current = null; // Ne restaurer qu'une fois
    }
  }, [loading, sessions.length]);

  // ═══ REALTIME — Supabase Realtime + polling fallback ═══
  useRealtimeSessions(
    isDemo ? null : 'active',
    handleSessionChange,
    loadTodaySessions,
    !isDemo && module === 'prep'
  );

  // Compteur 'P à renseigner' (réceptions transporteur avec anomalie sans P)
  const [pRefCount, setPRefCount] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
        const { count } = await supabase.from('elixir_receptions')
          .select('id', { count: 'exact', head: true })
          .eq('anomalie', true).is('p_ref', null).gte('created_at', since);
        setPRefCount(count || 0);
      } catch { setPRefCount(0); }
    })();
  }, [module]);

  // Load réclamation count for hub
  useEffect(() => {
    (async () => {
      // La pastille compte UNIQUEMENT ce qui reste à traiter : nouvelles +
      // prises en charge. Les réclamations « résolues » (solution choisie,
      // en attente de clôture) sont dans l'historique du module, pas dans
      // les listes actives — les compter faisait afficher 18 alors que le
      // module n'en montrait que 4.
      const { count } = await supabase.from('elixir_reclamations')
        .select('id', { count: 'exact', head: true })
        .in('status', ['nouveau', 'en_cours', 'en_attente', 'soumis_pr', 'valide_pr', 'non_approuve']);
      setReclamCount(count || 0);
    })();
  }, [module]);

  // Tickets service client : ouverts, et ceux qui dépassent 24 h ouvrées.
  // Le retard se calcule en heures ouvrées, donc un ticket ouvert vendredi
  // à 16 h n'est pas en retard lundi matin.
  //
  // Le droit d'accès est recalculé ICI et pas lu dans `isResp` : cette
  // constante est déclarée plus bas dans le composant, et un tableau de
  // dépendances est évalué PENDANT le rendu — la citer ici faisait planter
  // tout l'écran d'accueil avec « Cannot access … before initialization ».
  useEffect(() => {
    const role = user?.role;
    const autorise = ['responsable', 'pharmacien', 'admin'].includes(role)
      || (user?.modules || []).includes('tickets');
    if (!autorise) return;
    (async () => {
      const { data } = await supabase.from('elixir_tickets')
        .select('created_at, statut')
        .in('statut', ['nouveau', 'en_cours', 'en_attente']);
      const ouverts = data || [];
      setTicketsCount(ouverts.length);
      setTicketsRetard(ouverts.filter(t => heuresOuvrees(t.created_at) > DELAI_ALERTE_H).length);
    })();
  }, [module, user]);

  // Gestion de stock : cas ouverts (droit recalculé ici, jamais lu dans
  // isResp, déclarée plus bas — voir l'incident du 13/10/2026).
  useEffect(() => {
    const autorise = ['responsable', 'pharmacien', 'admin'].includes(user?.role) || (user?.modules || []).includes('stock_cas');
    if (!autorise) return;
    supabase.from('elixir_stock_cas').select('id', { count: 'exact', head: true })
      .in('statut', ['nouveau', 'en_verification', 'en_attente'])
      .then(({ count }) => setCasStockCount(count || 0), () => {});
    // Écarts sensibles à revoir : pharmaciens et admins seulement.
    if (['pharmacien', 'admin'].includes(user?.role)) {
      supabase.from('elixir_stock_cas').select('id', { count: 'exact', head: true })
        .eq('sensible', true).is('revue_at', null)
        .then(({ count, error }) => setSensiblesCount(error ? 0 : (count || 0)), () => {});
    }
  }, [module, user]);

  // Stupéfiants restant à compléter aujourd'hui (tuile)
  useEffect(() => {
    if (!isResp && !(user?.modules || []).includes('stups')) return;
    (async () => {
      try {
        const jour = new Date().toISOString().slice(0, 10);
        const { data: sessions } = await supabase.from('elixir_prep_sessions').select('id').eq('date', jour);
        const sids = (sessions || []).map(s => s.id);
        if (!sids.length) { setStupsCount(0); return; }
        const lignes = [];
        for (let i = 0; i < sids.length; i += 80) {
          const { data } = await supabase.from('elixir_prep_order_lines').select('session_id, product_id, qty').in('session_id', sids.slice(i, i + 80));
          lignes.push(...(data || []));
        }
        const avec = lignes.filter(l => Number(l.qty) > 0);
        const { getProductFlags } = await import('../lib/product-flags.js');
        const flags = await getProductFlags([...new Set(avec.map(l => l.product_id).filter(Boolean))]);
        const stup = avec.filter(l => flags[l.product_id]?.stup);
        const { data: done } = await supabase.from('elixir_stups_completions').select('cle').eq('jour', jour);
        const faits = new Set((done || []).map(d => d.cle));
        setStupsCount(stup.filter(l => !faits.has(l.session_id + '|' + l.product_id)).length);
      } catch { setStupsCount(0); }
    })();
  }, [module]);

  // Corrections de réception en attente (tuile Corrections réception)
  useEffect(() => {
    (async () => {
      const { count } = await supabase.from('elixir_corrections_reception').select('id', { count: 'exact', head: true }).eq('statut', 'a_corriger');
      setCorrectionsCount(count || 0);
    })();
  }, [module]);

  // Contrôles non conformes en attente du visa pharmacien (tuile Contrôle)
  useEffect(() => {
    if (!['responsable', 'pharmacien', 'admin'].includes(user?.role)) return;
    (async () => {
      const { data } = await supabase.from('elixir_controles_prep').select('created_at, controle_at')
        .eq('status', 'en_attente_visa').order('created_at', { ascending: true }).limit(200);
      const rows = data || [];
      setVisaCount(rows.length);
      setVisaOldestDays(rows.length ? Math.floor((Date.now() - new Date(rows[0].controle_at || rows[0].created_at).getTime()) / 86400000) : 0);
    })();
  }, [module]);

  // Alerte dérogations : nombre de nouvelles demandes depuis la dernière consultation
  useEffect(() => {
    (async () => {
      try {
        let lastSeen = null;
        try { lastSeen = localStorage.getItem('derog_last_seen'); } catch {}
        let q = supabase.from('elixir_derogations_territoire').select('id', { count: 'exact', head: true });
        if (lastSeen) q = q.gt('created_at', lastSeen);
        const { count } = await q;
        setDerogCount(count || 0);
      } catch { setDerogCount(0); }
    })();
  }, [module]);

  // Compteur livraisons du prochain samedi
  useEffect(() => {
    (async () => {
      try {
        const d = new Date(); d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
        const iso = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        const { count } = await supabase.from('elixir_livraisons_samedi').select('id', { count: 'exact', head: true }).eq('samedi_date', iso);
        setSamediCount(count || 0);
      } catch { setSamediCount(0); }
    })();
  }, [module]);

  // Load tournée order (rang unique)
  const [tourneeOrder, setTourneeOrder] = useState([]); // ['2', 'IMPERATIFS', '1', ...]

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.from('elixir_prep_tournee_priorities').select('*').order('priority', { ascending: true });
        if (data && data.length > 0) {
          setTourneeOrder(data.map(d => d.tournee));
        }
      } catch (e) { console.warn('Priorities table not found — using defaults'); }
    })();
  }, []);

  // Compute ordered tournées: saved order first, then any new ones not yet ranked
  function getOrderedTournees(rawTournees) {
    const ordered = [];
    // D'abord les tournées dans l'ordre sauvegardé
    tourneeOrder.forEach(t => { if (rawTournees.includes(t)) ordered.push(t); });
    // Puis les nouvelles tournées pas encore classées
    rawTournees.forEach(t => { if (!ordered.includes(t)) ordered.push(t); });
    return ordered;
  }

  function getRank(t, orderedList) {
    const idx = orderedList.indexOf(t);
    return idx >= 0 ? idx + 1 : 999;
  }

  async function moveTournee(tournee, direction, orderedList) {
    const idx = orderedList.indexOf(tournee);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= orderedList.length) return;

    const newOrder = [...orderedList];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    setTourneeOrder(newOrder);

    try {
      for (let i = 0; i < newOrder.length; i++) {
        await supabase.from('elixir_prep_tournee_priorities').upsert(
          { tournee: newOrder[i], priority: i + 1 },
          { onConflict: 'tournee' }
        );
      }
    } catch (e) { console.error('Save order error:', e); }
  }

  // ═══ PHARMACY REORDER (drag-and-drop) ═══
  function movePharmacy(tournee, fromIdx, toIdx) {
    const tSess = sessions.filter(s => (s.tournee || '') === tournee).sort((a, b) => (a.delivery_position || 999) - (b.delivery_position || 999));
    if (fromIdx < 0 || toIdx < 0 || fromIdx >= tSess.length || toIdx >= tSess.length) return;
    const reordered = [...tSess];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    // Update positions in state
    const updates = reordered.map((s, i) => ({ ...s, delivery_position: i + 1 }));
    setSessions(prev => {
      const newSessions = [...prev];
      updates.forEach(u => {
        const idx = newSessions.findIndex(s => s.id === u.id);
        if (idx >= 0) newSessions[idx] = { ...newSessions[idx], delivery_position: u.delivery_position };
      });
      return newSessions;
    });

    // Save to Supabase — session + permanent table (fire-and-forget)
    if (!isDemo) {
      Promise.all([
        // Save on today's sessions
        ...updates.map(u =>
          supabase.from('elixir_prep_sessions').update({ delivery_position: u.delivery_position }).eq('id', u.id)
        ),
        // Save permanent order (persists across days)
        ...updates.map(u =>
          supabase.from('elixir_prep_pharmacy_order').upsert(
            { tournee, partner_id: u.partner_id, position: u.delivery_position, updated_at: new Date().toISOString() },
            { onConflict: 'tournee,partner_id' }
          )
        ),
      ]).catch(e => console.warn('Save pharmacy order error:', e));
    }
  }

  function handleDragDrop(tournee) {
    if (dragItem === null || dragOverItem === null || dragItem === dragOverItem) {
      setDragItem(null); setDragOverItem(null); return;
    }
    movePharmacy(tournee, dragItem, dragOverItem);
    setDragItem(null); setDragOverItem(null);
  }

  // Filtrage et tri — Préparations = en_cours
  // Les pharmacies verrouillées = locked_at ET status prete (pas encore partie)
  const lockedPartnerIds = new Set(sessions.filter(s => s.locked_at && s.status === 'prete').map(s => s.partner_id));
  let filtered = sessions.filter(s => s.status === 'en_cours');
  let crossResults = []; // Sessions trouvées dans Départs
  if (search) {
    const q = search.toLowerCase();
    const matchFn = s =>
      s.partner_name?.toLowerCase().includes(q) ||
      (s.adresse || '').toLowerCase().includes(q) ||
      (s.partner_cip || '').toLowerCase().includes(q) ||
      (s.order_refs || []).some(r => r.toLowerCase().includes(q));
    filtered = filtered.filter(matchFn);
    // Chercher aussi dans Départs (prete + depart_valide)
    crossResults = sessions.filter(s => (s.status === 'prete' || s.status === 'depart_valide') && matchFn(s));
  }

  // Tri : statut (à préparer → en prépa → prête → partie) puis tournée puis nom
  function statusSortKey(s) {
    const hasColis = (s.nb_amb || 0) + (s.nb_froid || 0) + (s.nb_cartons || 0) + (s.nb_cartons_vrac || 0) > 0;
    if (s.status === 'en_cours' && !hasColis) return 1; // À préparer (en haut)
    if (s.status === 'en_cours' && hasColis) return 2;  // En prépa
    if (s.status === 'prete') return 3;                  // Prête
    if (s.status === 'depart_valide') return 4;          // Partie
    return 5;
  }
  filtered = [...filtered].sort((a, b) => {
    const sa = statusSortKey(a);
    const sb = statusSortKey(b);
    if (sa !== sb) return sa - sb;
    const ta = tourneeSortKey(a.tournee);
    const tb = tourneeSortKey(b.tournee);
    if (ta !== tb) return ta - tb;
    return a.partner_name.localeCompare(b.partner_name);
  });

  // Tournées disponibles — triées par rang défini par le responsable
  const rawTournees = [...new Set(sessions.map(s => s.tournee || ''))];
  const allTournees = getOrderedTournees(rawTournees);

  // Auto-collapse: tournées 100% terminées repliées, première avec travail ouverte
  useEffect(() => {
    if (allTournees.length === 0) return;
    const prepOnly = sessions.filter(s => s.status !== 'prete' && s.status !== 'depart_valide');
    const init = {};
    let firstOpenSet = false;
    allTournees.forEach(t => {
      const tSessions = prepOnly.filter(s => (s.tournee || '') === t);
      if (tSessions.length === 0) {
        init[t] = true; // replié — rien à faire
      } else {
        const allDone = tSessions.every(s => s.status === 'prete');
        if (allDone) {
          init[t] = true;
        } else if (!firstOpenSet) {
          init[t] = false;
          firstOpenSet = true;
        } else {
          init[t] = true;
        }
      }
    });
    setCollapsedT(init);
  }, [allTournees.length, sessions.filter(s => s.status === 'en_cours').length, sessions.filter(s => s.status === 'prete').length]);
  function toggleT(t) { setCollapsedT(p => ({ ...p, [t]: !p?.[t] })); }
  function isClosed(t) { return collapsedT === null || collapsedT?.[t]; }

  // Stats
  const prepSessions = sessions.filter(s => s.status !== 'prete' && s.status !== 'depart_valide');
  const total = prepSessions.length;
  const totalOrders = prepSessions.reduce((sum, s) => sum + (s.order_refs || []).filter(r => !r.startsWith('[R') && r !== 'MANUEL').length, 0);
  const totalReliquats = prepSessions.reduce((sum, s) => sum + (s.order_refs || []).filter(r => r.startsWith('[R')).length, 0);

  const initials = user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const isResp = ['responsable', 'pharmacien', 'admin'].includes(user.role);
  const todayLabel = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  function handleSynced() { setLoading(true); loadTodaySessions(); }

  // ↻ Actualiser (header) : feedback visuel + recharge Supabase immédiate
  // + déclenche le worker Odoo en arrière-plan (comme au montage), puis
  // recharge quand le worker a eu le temps d'écrire (~12s).
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  async function handleHeaderRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshMsg('Actualisation…');
    try {
      await loadTodaySessions();
      // Déclencher la synchro Odoo en arrière-plan (skip si déjà en cours côté worker)
      let odooTriggered = false;
      try {
        const res = await fetch('/.netlify/functions/odoo-sync-trigger', { method: 'POST' });
        odooTriggered = res.ok;
      } catch { /* offline ou erreur : on garde les données Supabase */ }
      if (odooTriggered) {
        setRefreshMsg('Synchro Odoo en cours…');
        setTimeout(async () => {
          await loadTodaySessions();
          setRefreshMsg('✓ Actualisé');
          setTimeout(() => setRefreshMsg(null), 2000);
          setRefreshing(false);
        }, 12000);
        return; // la fin du spinner est gérée dans le timeout
      }
      setRefreshMsg('✓ Actualisé');
      setTimeout(() => setRefreshMsg(null), 2000);
    } catch (e) {
      setRefreshMsg('❌ Erreur : ' + (e.message || 'actualisation'));
      setTimeout(() => setRefreshMsg(null), 3000);
    }
    setRefreshing(false);
  }

  function statusBadge(s) {
    if (s.status === 'depart_valide') return { cls: 'badge-blue', txt: 'Partie' };
    if (s.status === 'prete') return s.has_new_orders
      ? { cls: '', txt: '⚠️ Prête + Nouvelle cmd', style: { background: '#FDE68A', color: '#92400E', fontWeight: 700 } }
      : { cls: 'badge-green', txt: 'Prête' };
    if (s.prepared_by || (s.nb_amb || 0) > 0 || (s.nb_froid || 0) > 0 || (s.nb_cartons || 0) > 0 || (s.nb_cartons_vrac || 0) > 0)
      return s.has_new_orders
        ? { cls: '', txt: '⚠️ En prépa + Nouvelle cmd', style: { background: '#FDE68A', color: '#92400E', fontWeight: 700 } }
        : { cls: 'badge-orange', txt: 'En prépa' };
    return { cls: '', txt: 'À préparer', style: { background: '#F0F0F0', color: '#888' } };
  }

  function invoiceBadge(s) {
    const refs = s.invoice_refs || [];
    if (!refs.length) return null;
    return refs;
  }

  async function quickClaim(e, s) {
    e.stopPropagation();
    if (s.prepared_by || s.status === 'prete' || s.status === 'depart_valide') return;
    if (String(s.id).startsWith('demo-')) return;

    // 1. Vérifier côté serveur que la session n'est pas déjà prise
    const { data: current } = await supabase.from('elixir_prep_sessions')
      .select('prepared_by, prepared_by_name').eq('id', s.id).single();
    if (current && current.prepared_by && current.prepared_by !== user.id) {
      alert('Déjà pris par ' + (current.prepared_by_name || 'un autre magasinier'));
      // Refresh pour mettre à jour l'affichage
      loadTodaySessions();
      return;
    }
    
    // 2. Vérifier côté serveur si j'ai déjà une prépa en cours
    const { data: myOthers } = await supabase.from('elixir_prep_sessions')
      .select('id, partner_name')
      .eq('prepared_by', user.id)
      .eq('status', 'en_cours')
      .neq('id', s.id);

    if (myOthers && myOthers.length > 0) {
      alert('Tu as déjà une préparation en cours : ' + myOthers[0].partner_name + '\nValide-la en "Prête" ou libère-la avant d\'en prendre une autre.');
      return;
    }
    
    const claimed = { ...s, prepared_by: user.id, prepared_by_name: user.name, status: 'en_cours', has_new_orders: false };
    
    // Persist to Supabase FIRST (not optimistic — avoid showing panel before write succeeds)
    const { error: writeErr } = await supabase.from('elixir_prep_sessions').update({
      prepared_by: user.id,
      prepared_by_name: user.name,
      status: 'en_cours',
      has_new_orders: false,
      updated_at: new Date().toISOString(),
      claimed_at: new Date().toISOString(),   // heure de prise en main (libération après 1 h sans activité)
    }).eq('id', s.id).is('prepared_by', null); // Atomic: only if still unclaimed
    
    if (writeErr) { alert('Erreur : ' + writeErr.message); return; }

    // Verify it's mine (race condition check)
    const { data: check } = await supabase.from('elixir_prep_sessions')
      .select('prepared_by').eq('id', s.id).single();
    if (check && check.prepared_by !== user.id) {
      loadTodaySessions();
      alert('Déjà pris par un autre magasinier');
      return;
    }

    // Now update UI
    setSessions(prev => prev.map(sess => sess.id === s.id ? claimed : sess));
    openQuickPanel(claimed);
  }

  // ═══ ORDRE DES MODULES ═══
  // L'ordre suit le travail réel de la journée, pas l'ordre d'ajout au code.
  // Les modules du flux pharmacie d'abord, dans l'ordre où on les ouvre :
  // on prépare, on réceptionne, on complète les stups, on contrôle, on
  // traite ce qui revient (réclamations, dérogations). Puis l'export, puis
  // le pilotage, puis l'administration. Un module se déplace en changeant
  // sa ligne de place ici — nulle part ailleurs.
  const ALL_MODULES = [
    // ── Pharmacies : le flux quotidien ──
    { g: 'phie', id: 'prep', label: 'Préparation pharmacies', count: totalOrders + totalReliquats, ready: true },
    { g: 'phie', id: 'reception_t', label: 'Réception transporteurs', count: pRefCount > 0 ? pRefCount : null, urgent: pRefCount > 0, urgentLabel: 'P à renseigner', ready: true },
    { g: 'phie', id: 'reception_p', label: 'Réception produits', count: null, ready: true },
    { g: 'phie', id: 'corrections', label: 'Corrections réception', count: correctionsCount > 0 ? correctionsCount : null, urgent: correctionsCount > 0, urgentLabel: 'à corriger', ready: true },
    { g: 'phie', id: 'stups', label: 'Stupéfiants à compléter', count: stupsCount > 0 ? stupsCount : null, urgent: stupsCount > 0, urgentLabel: 'à compléter', ready: true },
    { g: 'phie', id: 'livraison_samedi', label: 'Livraisons du samedi', count: samediCount > 0 ? samediCount : null, ready: true },
    { g: 'phie', id: 'controle_prep', label: 'Contrôle préparation', count: visaCount > 0 ? visaCount : null, urgent: visaCount > 0, urgentLabel: visaOldestDays >= 2 ? 'visa(s) en attente depuis ' + visaOldestDays + ' j' : 'en attente de visa', ready: true },
    { g: 'phie', id: 'reclam', label: 'Réclamations pharmacies', count: reclamCount, ready: true },
    { g: 'phie', id: 'tickets', label: 'Tickets service client', count: ticketsCount > 0 ? ticketsCount : null, urgent: ticketsRetard > 0, urgentLabel: ticketsRetard + ' au-delà de 24 h', ready: true },
    { g: 'phie', id: 'derogations', label: 'Dérogations hors secteur', count: derogCount > 0 ? derogCount : null, urgent: derogCount > 0, urgentLabel: 'nouvelle(s)', ready: true },

    // ── Export ──
    { g: 'export', id: 'export', label: 'Préparation export', count: null, ready: true },
    { g: 'export', id: 'litiges', label: 'Litiges clients export', count: null, ready: true },

    // ── Pilotage ──
    { g: 'suivi', id: 'stock_cas', label: 'Gestion de stock', count: casStockCount > 0 ? casStockCount : null, urgent: sensiblesCount > 0, urgentLabel: sensiblesCount + ' écart(s) sensible(s) à revoir', ready: true },
    { g: 'suivi', id: 'stats', label: 'Statistiques', count: null, ready: true },
    { g: 'suivi', id: 'impression', label: 'Impression des fiches qualité', count: null, ready: true },
    { g: 'suivi', id: 'historique', label: 'Historique & suivi', count: null, ready: true },

    // ── Administration ──
    { g: 'admin', id: 'equipe', label: 'Équipe & permissions', count: null, ready: true },

    // ── Version 2, en test : jamais mélangée aux modules de production ──
    { g: 'v2', id: 'reception_v2', label: 'Réception V2', count: null, ready: true },
    { g: 'v2', id: 'prep_v2', label: 'Prépa V2 (beta)', count: null, ready: true },
  ];

  // Libellé et couleur de chaque groupe, dans l'ordre d'affichage.
  const GROUPES = [
    { g: 'phie', titre: 'Pharmacies', couleur: '#1A5276' },
    { g: 'export', titre: 'Export', couleur: '#7D5A0A' },
    { g: 'suivi', titre: 'Suivi & pilotage', couleur: '#1E6B3C' },
    { g: 'admin', titre: 'Administration', couleur: '#5B21B6' },
    { g: 'v2', titre: 'Version 2 — en test', couleur: '#9CA3AF' },
  ];

  // Prépa V2 beta — accès limité à Zineb + Sam pendant la phase de test
  const isPrepV2BetaTester = (user?.name || '').toLowerCase().match(/zineb|sam/i);

  // Filtrer les modules selon les autorisations de l'utilisateur
  const userModules = user?.modules || ['prep', 'reception_t', 'reception_p'];
  // ═══ RÈGLE D'ACCÈS UNIQUE (tuiles ET garde) ═══
  // Une seule fonction décide qui voit / ouvre un module. Ajouter un module
  // = une ligne ici, jamais deux endroits à synchroniser.
  //   'beta'   → Zineb + Sam uniquement (prep_v2)
  //   'resp'   → responsables/pharmaciens/admin par rôle, sans case à cocher
  //   'resp+'  → responsables/pharmaciens/admin OU case cochée sur le compte
  //   'admin+' → admin OU case cochée (accès nominatif restreint)
  //   défaut   → case cochée sur le compte
  const ACL = {
    prep_v2: 'beta',
    controle_prep: 'resp', stats: 'resp', stups: 'resp+',
    derogations: 'resp+', livraison_samedi: 'resp+', litiges: 'resp+', tickets: 'resp+', stock_cas: 'resp+', impression: 'resp+',
    erreurs: 'admin+', equipe: 'admin+',
    corrections: 'recep+', // entrepôt : responsables OU module réception produits
  };
  const canAccess = (id) => {
    if (isDemo) return true;
    switch (ACL[id]) {
      case 'beta': return isPrepV2BetaTester;
      case 'resp': return isResp;
      case 'resp+': return isResp || userModules.includes(id);
      case 'admin+': return user?.role === 'admin' || userModules.includes(id);
      case 'recep+': return isResp || userModules.includes('reception_p') || userModules.includes(id);
      default: return userModules.includes(id);
    }
  };
  const MODULES = ALL_MODULES.filter(m => canAccess(m.id));

  const MOD_COLOR = '#7C3AED'; // conservé : couleur d'accent historique, encore utilisée hors hub

  // ═══ PROTECTION — module non autorisé → retour hub ═══
  useEffect(() => {
    // Même règle que les tuiles : un module non autorisé renvoie au hub
    if (module && !canAccess(module)) setModule(null);
  }, [module]);

  // ═══ "EN CONSTRUCTION" MODULE ═══
  if ([].includes(module)) {
    const mod = MODULES.find(m => m.id === module);
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">{mod?.label || 'Module'}</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content" style={{ textAlign: 'center', paddingTop: 80 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚧</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1A1D23', marginBottom: 6 }}>{mod?.label}</div>
          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>Ce module est en cours de développement.<br />Il sera bientôt disponible.</div>
          <button onClick={guardedLeave} className="btn btn-primary" style={{ margin: '0 auto', fontSize: 14 }}>← Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  // ═══ STUPÉFIANTS À COMPLÉTER ═══
  if (module === 'stups') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Stupéfiants à compléter</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><StupsTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ STATISTIQUES MODULE ═══
  if (module === 'stats') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Statistiques</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><StatsTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ CORRECTIONS RÉCEPTION MODULE ═══
  if (module === 'corrections') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Corrections réception</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><CorrectionsReceptionTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ SUIVI DES ERREURS MODULE ═══
  if (module === 'erreurs') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Suivi des erreurs</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><SuiviErreursTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ LITIGES CLIENTS EXPORT MODULE ═══
  if (module === 'litiges') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Litiges clients export</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><LitigesExportTab user={user} /></Lazy>
        </div>
      </div>
    );
  }



  // ═══ HUB VIEW ═══
  if (module === null) {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        {isDemo && <div style={{ background: '#D4680A', padding: '6px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>MODE DÉMO — données fictives, tu peux tout tester</div>}
        <ModuleTutorial key={tutoKey} moduleId="global" steps={TUTO_GLOBAL} />
        <div className="header">
          <div className="header-brand">ELIXIR PREP</div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
            <button className="header-logout" onClick={onLogout}>Changer</button>
          </div>
        </div>
        <div className="content">
          {GROUPES.map(grp => {
            const mods = MODULES.filter(m => m.g === grp.g);
            if (!mods.length) return null;
            return (
              <div key={grp.g} style={{ marginBottom: 14 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '14px 4px 7px',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: grp.couleur, textTransform: 'uppercase', letterSpacing: '.06em' }}>{grp.titre}</span>
                  <span style={{ flex: 1, height: 1, background: '#E8E8EE' }} />
                </div>
                {mods.map(m => (
                  <div key={m.id}
                    onClick={() => navigateToModule(m.id)}
                    style={{
                      display: 'flex', alignItems: 'center', padding: '16px 16px',
                      background: '#FFF', borderRadius: 8, marginBottom: 6,
                      borderLeft: '4px solid ' + grp.couleur,
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                    <div style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#1A1D23' }}>
                      {m.label}
                      {m.urgent && <div style={{ fontSize: 10, fontWeight: 700, color: '#C0392B', marginTop: 2 }}>🔴 {m.urgentLabel}</div>}
                    </div>
                    {m.count !== null ? (
                      <div style={{ minWidth: 32, height: 26, borderRadius: 13, background: m.urgent ? '#C0392B' : grp.couleur, color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, padding: '0 10px' }}>{m.count}</div>
                    ) : !m.ready ? (
                      <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>En construction</div>
                    ) : null}
                  </div>
                ))}
              </div>
            );
          })}
          <div style={{ marginTop: 16, padding: '0 4px' }}>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>Accès rapide</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button onClick={() => setOdooOpen(true)} className="btn btn-sm btn-outline" style={{ fontSize: 11 }}>🔄 Sync Odoo</button>
              <a href="/reclamation" target="_blank" rel="noopener noreferrer" style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#FFF', fontSize: 11, fontWeight: 500, color: '#6B7280', textDecoration: 'none' }}>📋 Formulaire réclamation</a>
              <button onClick={() => { resetAllTutorials(); setTutoKey(k => k + 1); }} style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#FFF', fontSize: 11, fontWeight: 500, color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit' }}>📖 Tutoriel</button>
            </div>
          </div>
        </div>
        <OdooSync open={odooOpen} onClose={() => setOdooOpen(false)} onSynced={handleSynced} />
      </div>
    );
  }

  // ═══ RÉCEPTION TRANSPORTEURS MODULE ═══
  if (module === 'reception_t') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Réception transporteurs</div>
          </div>
          <div className="header-user">
            <button onClick={() => { resetModuleTutorial('reception_t'); setTutoKey(k => k + 1); }} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 16, cursor: 'pointer', padding: '4px 8px' }}>📖</button>
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <ModuleTutorial key={'rt' + tutoKey} moduleId="reception_t" steps={TUTO_RECEPTION_T} />
          <Lazy cle={module}><ReceptionTransporteurs user={user} onBack={guardedLeave} onUnsavedChange={v => { moduleUnsavedRef.current = v; }} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ RÉCLAMATIONS MODULE ═══
  if (module === 'reclam') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Réclamations pharmacies</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><ReclamationsTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ GESTION DE STOCK MODULE ═══
  if (module === 'stock_cas') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Gestion de stock</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><StockCasTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  if (module === 'impression') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Impression des fiches</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><ImpressionFiches user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ TICKETS SERVICE CLIENT MODULE ═══
  if (module === 'tickets') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Tickets service client</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><TicketsTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ DÉROGATIONS HORS SECTEUR MODULE ═══
  if (module === 'derogations') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Dérogations hors secteur</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><DerogationsTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ LIVRAISONS DU SAMEDI MODULE ═══
  if (module === 'livraison_samedi') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Livraisons du samedi</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><LivraisonSamediTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ RÉCEPTION PRODUITS MODULE ═══
  if (module === 'reception_p') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Réception produits</div>
          </div>
          <div className="header-user">
            <button onClick={() => { resetModuleTutorial('reception_p'); setTutoKey(k => k + 1); }} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 16, cursor: 'pointer', padding: '4px 8px' }}>📖</button>
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <ModuleTutorial key={'rp' + tutoKey} moduleId="reception_p" steps={TUTO_RECEPTION_P} />
          <Lazy cle={module}><ReceptionProduits user={user} onBack={guardedLeave} onUnsavedChange={v => { moduleUnsavedRef.current = v; }} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ RÉCEPTION V2 MODULE (isolé, phase test) ═══
  if (module === 'reception_v2') {
    return (
      <Lazy cle={module}>
        <ReceptionV2App user={user} onBack={() => { setModule(null); window.location.hash = ''; }} />
      </Lazy>
    );
  }

  // ═══ PRÉPA V2 MODULE (isolé, beta Zineb + Sam) ═══
  if (module === 'prep_v2') {
    return (
      <Lazy cle={module}>
        <PrepV2App user={user} onBack={() => { setModule(null); window.location.hash = ''; }} />
      </Lazy>
    );
  }

  // ═══ EXPORT MODULE ═══
  if (module === 'export') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Préparation export</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><ExportPrep user={user} onBack={guardedLeave} onUnsavedChange={v => { moduleUnsavedRef.current = v; }} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ TABLEAU DE BORD TOURNÉES ═══
  // ═══ CONTRÔLE PRÉPARATION ═══
  if (module === 'controle_prep') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Contrôle préparation</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: '#1E6B3C' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><ControlePrepTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ ÉQUIPE MODULE ═══
  if (module === 'equipe') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Équipe & permissions</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: '#1E6B3C' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><EquipeTab /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ HISTORIQUE MODULE ═══
  if (module === 'historique') {
    return (
      <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div className="header-brand">Historique & suivi</div>
          </div>
          <div className="header-user">
            <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
            <span>{user.name}</span>
          </div>
        </div>
        <div className="content">
          <Lazy cle={module}><HistoriqueTab user={user} /></Lazy>
        </div>
      </div>
    );
  }

  // ═══ PRÉPARATION PHARMACIES MODULE (module === 'prep') ═══
  return (
    <div style={{ minHeight: '100vh', background: '#F2F3F7' }}>
      {isDemo && <div style={{ background: '#D4680A', padding: '6px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>MODE DÉMO</div>}
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={guardedLeave} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 24, cursor: 'pointer', padding: '8px 12px', minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
          <div className="header-brand">Préparation pharmacies</div>
        </div>
        <div className="header-user">
          <button onClick={() => { resetModuleTutorial('prep'); setPrepTutoKey(k => k + 1); }} style={{ background: 'none', border: 'none', color: '#FFF', fontSize: 16, cursor: 'pointer', padding: '4px 8px' }}>📖</button>
          <button onClick={handleHeaderRefresh} title="Actualiser" disabled={refreshing} style={{
            background: 'none', border: 'none', color: '#FFF', fontSize: 16,
            cursor: refreshing ? 'wait' : 'pointer', padding: '4px 8px',
            opacity: refreshing ? 1 : 0.7,
            display: 'inline-block',
            animation: refreshing ? 'refreshSpin 0.9s linear infinite' : 'none',
          }}>↻</button>
          <div className="header-avatar" style={{ background: isResp ? '#1E6B3C' : '#1A5276' }}>{initials}</div>
          <span>{user.name}</span>
          <button className="header-logout" onClick={onLogout}>Changer</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #E5E7EB', background: 'white', padding: '0 16px', position: 'sticky', top: 48, zIndex: 90 }}>
        <button className={'tab-btn' + (tab === 'pharma' ? ' active' : '')} onClick={() => navigateToTab('pharma')}>Préparations</button>
        <button className={'tab-btn' + (tab === 'departs' ? ' active' : '')} onClick={() => navigateToTab('departs')}>Départs</button>
        <button className={'tab-btn' + (tab === 'caisses' ? ' active' : '')} onClick={() => navigateToTab('caisses')}>Caisses</button>
        {isResp && (
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <button className={'tab-btn' + (['reliquats', 'anomalies'].includes(tab) ? ' active' : '')}
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              style={{ minWidth: 36, padding: '0 8px' }}>
              {['reliquats', 'anomalies'].includes(tab)
                ? (tab === 'reliquats' ? 'Reliquats' : 'Anomalies')
                : '•••'}
            </button>
            {moreMenuOpen && (
              <>
                <div onClick={() => setMoreMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }}></div>
                <div style={{
                  position: 'absolute', left: 0, top: '100%', marginTop: 4, zIndex: 50,
                  background: 'white', borderRadius: 10, padding: '4px 0',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #E5E7EB',
                  minWidth: 160, whiteSpace: 'nowrap',
                }}>
                  {[
                    { id: 'anomalies', label: 'Anomalies' },
                    { id: 'reliquats', label: 'Reliquats', color: '#922B21' },
                  ].map(m => (
                    <div key={m.id} onClick={() => { navigateToTab(m.id); setMoreMenuOpen(false); }}
                      style={{
                        padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        color: tab === m.id ? '#1A5276' : (m.color || '#374151'),
                        background: tab === m.id ? '#F3F4F6' : 'transparent',
                      }}>
                      {m.label}
                      {tab === m.id && <span style={{ float: 'right', color: '#16A34A' }}>✓</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <OfflineBanner />

      {refreshMsg && (
        <div style={{
          position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)',
          padding: '8px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600,
          color: 'white', zIndex: 300, boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          background: refreshMsg.startsWith('❌') ? '#C0392B' : refreshMsg.startsWith('✓') ? '#1E6B3C' : '#1A5276',
          display: 'flex', alignItems: 'center', gap: 8,
          animation: 'toastIn 0.15s ease-out forwards',
        }}>
          {!refreshMsg.startsWith('✓') && !refreshMsg.startsWith('❌') && (
            <span style={{ display: 'inline-block', animation: 'refreshSpin 0.9s linear infinite' }}>↻</span>
          )}
          {refreshMsg}
        </div>
      )}

      <PullToRefresh onRefresh={loadTodaySessions}>
      <div className="content">
        {tab === 'pharma' && selectedSession && (
          <PrepDetail
            session={selectedSession}
            user={user}
            reliquats={pendingReliquats[selectedSession.partner_id] || []}
            initialStep={selectedInitialStep}
            onBack={() => closeDetail()}
            onSessionUpdate={(updated) => {
              setSelectedSession(updated);
              setSessions(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
            }}
          />
        )}

        {tab === 'pharma' && !selectedSession && <>
          <ModuleTutorial key={'prep' + prepTutoKey} moduleId="prep" steps={TUTO_PREP} />
          <div style={{ position: 'sticky', top: 88, zIndex: 80, background: '#F2F3F7', paddingTop: 4, paddingBottom: 6 }}>
            <input className="input" type="text" placeholder="Rechercher… (nom, adresse, n° commande)"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="stats-row">
            <div className="stat-card"><div className="stat-num">{total}</div><div className="stat-label">PHARMACIES</div></div>
            <div className="stat-card"><div className="stat-num" style={{ color: '#D97706' }}>{totalOrders}</div><div className="stat-label">COMMANDES</div></div>
            <div className="stat-card"><div className="stat-num" style={{ color: '#C0392B' }}>{totalReliquats}</div><div className="stat-label">RELIQUATS</div></div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div className="section-title" style={{ textTransform: 'capitalize', margin: 0 }}>{todayLabel}</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-blue btn-sm" onClick={() => setShowAddPrepModal(true)}>➕ Ajouter</button>
              <button className="btn btn-blue btn-sm" onClick={() => setOdooOpen(true)}>🔄 Odoo</button>
            </div>
          </div>

          {loading && <div className="empty-state"><div className="empty-state-text">Chargement…</div></div>}

          {!loading && sessions.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <div className="empty-state-text">Aucune préparation aujourd'hui.<br/>Synchronise depuis Odoo.</div>
              <button className="btn btn-dark" style={{ marginTop: 16, width: 'auto', display: 'inline-block' }}
                onClick={() => setOdooOpen(true)}>🔗 Synchroniser Odoo</button>
            </div>
          )}

          {/* ═══ MES PRÉPARATIONS — en haut ═══ */}
          {!loading && (() => {
            const myPreps = sessions.filter(s => s.prepared_by === user?.id && s.status === 'en_cours');
            if (myPreps.length === 0) return null;
            return (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#EEF6FF', borderRadius: 12, border: '1.5px solid #B3D4F0' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1A5276', marginBottom: 8 }}>
                  🔧 Mes préparations ({myPreps.length})
                </div>
                {myPreps.map(s => {
                  const badge = statusBadge(s);
                  return (
                    <div key={s.id}
                      onClick={() => openQuickPanel(s)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 12px', background: 'white', borderRadius: 10,
                        border: '1px solid #D6E4F0', marginBottom: 4, cursor: 'pointer',
                      }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.partner_name}</div>
                        <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>
                          {(s.order_refs || []).includes('MANUEL') && !(s.order_refs || []).some(r => r !== 'MANUEL' && !r.startsWith('[R'))
                            ? 'Ajout manuel'
                            : (s.order_refs || []).filter(r => !r.startsWith('[R') && r !== 'MANUEL').length + ' commande' + ((s.order_refs || []).filter(r => !r.startsWith('[R') && r !== 'MANUEL').length > 1 ? 's' : '')}
                          {s.tournee ? ' · T' + s.tournee : ''}
                        </div>
                      </div>
                      <span className={'badge ' + badge.cls} style={badge.style || {}}>{badge.txt}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Sections repliables par tournée */}
          {!loading && allTournees.map((t, tIdx) => {
            const tColor = TOURNEE_COLORS[t] || '#888';
            const tLabel = t === 'IMPERATIFS' ? 'Impératifs' : t === 'CHRONOPOST' ? 'Chronopost' : t ? 'Tournée ' + t : 'Sans tournée';
            const tSessions = filtered.filter(s => (s.tournee || '') === t).sort((a, b) => {
              // 1. Statut : à préparer → en cours → prêtes → parties
              const statusOrder = (s) => {
                if (s.status === 'en_cours' && !s.prepared_by) return 0; // à préparer
                if (s.status === 'en_cours' && s.prepared_by) return 1;  // en cours
                if (s.status === 'prete') return 2;
                if (s.status === 'depart_valide') return 3;
                return 4;
              };
              const sA = statusOrder(a), sB = statusOrder(b);
              if (sA !== sB) return sA - sB;
              // 2. Position de livraison
              const posA = a.delivery_position || 999;
              const posB = b.delivery_position || 999;
              if (posA !== posB) return posA - posB;
              // 3. Nom pharmacie
              return (a.partner_name || '').localeCompare(b.partner_name || '');
            });
            if (search && tSessions.length === 0) return null;
            const tAll = filtered.filter(s => (s.tournee || '') === t);
            if (!search && tAll.length === 0) return null;
            const tAP = tAll.filter(s => s.status === 'en_cours' && !s.prepared_by).length;
            const tAF = tAll.filter(s => s.status === 'en_cours' && s.prepared_by).length;
            const tPrete = tAll.filter(s => s.status === 'prete').length;
            const tAllDone = tAll.length > 0 && tAP === 0 && tAF === 0;
            const closed = isClosed(t);

            const isFirst = tIdx === 0;
            const isLast = tIdx === allTournees.length - 1;

            // Progression
            const tTotal = tAll.length;
            const tDone = tPrete;
            const tStarted = tAF + tDone;
            const showProgress = tTotal > 0 && tStarted > 0;
            const progressRatio = tTotal > 0 ? tDone / tTotal : 0;

            return (
              <div key={t || '__none'} style={{ marginBottom: 2 }}>
                <div style={{ padding: '10px 4px', borderBottom: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                    {/* Chevron + badge couleur + nom — cliquable pour collapse */}
                    <div onClick={() => toggleT(t)} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer', userSelect: 'none', minWidth: 0 }}>
                      <span style={{ fontSize: 10, color: '#6B7280', transform: closed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▼</span>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', background: tColor,
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>{t === 'IMPERATIFS' ? '!' : t === 'CHRONOPOST' ? 'CH' : (t || '?')}</div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: tAllDone ? '#16A34A' : '#1A1D23' }}>
                        {tAllDone ? '✅ ' : ''}{tLabel}
                      </span>
                      <span style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        — {tAll.length}
                        {tAP > 0 && <span style={{ color: '#888' }}>{tAP} à faire</span>}
                        {tAF > 0 && <span style={{ color: '#D97706' }}>{tAF} en cours</span>}
                        {tPrete > 0 && <span style={{ color: '#16A34A' }}>{tPrete} prête{tPrete > 1 ? 's' : ''}</span>}
                      </span>
                    </div>
                    {isResp && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flexShrink: 0 }}>
                        <button onClick={(e) => { e.stopPropagation(); moveTournee(t, -1, allTournees); }} disabled={isFirst}
                          style={{ background: 'none', border: 'none', fontSize: 10, cursor: isFirst ? 'default' : 'pointer', color: isFirst ? '#E5E7EB' : '#1A5276', padding: '0 4px', lineHeight: 1 }}>▲</button>
                        <button onClick={(e) => { e.stopPropagation(); moveTournee(t, 1, allTournees); }} disabled={isLast}
                          style={{ background: 'none', border: 'none', fontSize: 10, cursor: isLast ? 'default' : 'pointer', color: isLast ? '#E5E7EB' : '#1A5276', padding: '0 4px', lineHeight: 1 }}>▼</button>
                      </div>
                    )}
                  </div>

                  {/* Barre de progression — seulement si au moins une commencée */}
                  {showProgress && (
                    <div style={{ marginTop: 6, marginLeft: 40, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#E5E7EB', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 2, background: progressRatio >= 1 ? '#16A34A' : '#6B7280', width: (progressRatio * 100) + '%', transition: 'width 0.3s ease' }}></div>
                      </div>
                      <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 500, flexShrink: 0 }}>{tDone} / {tTotal} préparées</span>
                    </div>
                  )}
                </div>

                {/* Section content */}
                {!closed && (
                  <div>
                    {tSessions.length === 0 ? (
                      <div style={{ padding: '12px 28px', fontSize: 12, color: '#D1D5DB', fontStyle: 'italic' }}>Aucune pharmacie</div>
                    ) : tSessions.map((s, sIdx) => {
                      const badge = statusBadge(s);
                      return (
                        <div key={s.id} className="card" style={{
                          cursor: 'pointer', marginLeft: 8, marginBottom: 4,
                          borderLeft: '3px solid ' + tColor,
                          background: s.has_new_orders ? '#FFFBEB' : s.prepared_by && s.status === 'en_cours' ? '#FFF8F0' : s.status === 'prete' ? '#EEFAF0' : 'white',
                          transition: 'background 0.15s',
                          display: 'flex', alignItems: 'stretch', gap: 0,
                        }}
                          onClick={() => openDetail(s)}
                        >

                          <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Alerte nouvelle commande sur session avec colis existants */}
                          {s.has_new_orders && ((s.nb_amb || 0) + (s.nb_froid || 0) + (s.nb_cartons || 0) + (s.nb_cartons_vrac || 0) > 0) && (
                            <div style={{
                              padding: '6px 10px', borderRadius: 6, marginBottom: 6,
                              background: '#FDE68A', border: '1px solid #F59E0B', fontSize: 11, fontWeight: 700, color: '#92400E',
                            }}>
                              ⚠️ Nouvelle commande — déjà {[
                                (s.nb_amb || 0) > 0 && (s.nb_amb + ' amb'),
                                (s.nb_froid || 0) > 0 && (s.nb_froid + ' froid'),
                                (s.nb_cartons || 0) > 0 && (s.nb_cartons + ' std'),
                                (s.nb_cartons_vrac || 0) > 0 && (s.nb_cartons_vrac + ' vrac'),
                              ].filter(Boolean).join(' + ')} prêts
                              {s.prepared_by_name ? ' par ' + s.prepared_by_name : ''}
                            </div>
                          )}
                          {/* En cours par quelqu'un */}
                          {s.prepared_by && s.status === 'en_cours' && !s.has_new_orders && (
                            <div style={{
                              padding: '4px 10px', borderRadius: 6, marginBottom: 6,
                              background: '#D4680A', color: 'white', fontSize: 11, fontWeight: 700,
                              display: 'inline-block',
                            }}>
                              🔧 En cours par {s.prepared_by_name}
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div className="card-title">{s.partner_name}</div>
                              <div className="card-sub">{s.adresse || ''}</div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              {!s.prepared_by && s.status !== 'prete' && s.status !== 'depart_valide' ? (
                                <button
                                  onClick={(e) => quickClaim(e, s)}
                                  style={{
                                    padding: '6px 12px', borderRadius: 8, border: '1.5px solid #1A5276',
                                    background: 'white', color: '#1A5276', fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                                  }}
                                  onTouchStart={(e) => e.currentTarget.style.background = '#1A5276'}
                                  onTouchEnd={(e) => { e.currentTarget.style.background = 'white'; }}
                                >▶ Je prends</button>
                              ) : (
                                <span className={'badge ' + badge.cls} style={badge.style || {}}>{badge.txt}</span>
                              )}
                              {invoiceBadge(s) && (
                                <div style={{ marginTop: 2 }}>
                                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: '#FEF3CD', color: '#7D5A0A' }}>
                                    ⚠️ Fact. manquante : {invoiceBadge(s).join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {s.order_refs && s.order_refs.filter(r => !r.startsWith('[R') && r !== 'MANUEL').length > 0 && (
                            <div className="refs">
                              {s.order_refs.filter(r => !r.startsWith('[R') && r !== 'MANUEL').map(r => {
                                const isInvoiceMissing = (s.invoice_refs || []).includes(r);
                                const refDate = (s.order_refs_dates || {})[r];
                                const timeStr = refDate ? (() => { try { const d = new Date(refDate.includes('T') ? refDate : refDate.replace(' ', 'T') + 'Z'); return isNaN(d) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } })() : '';
                                return <span key={r} className="ref-tag" style={isInvoiceMissing ? { background: '#FEF3CD', color: '#7D5A0A', border: '1px solid #F5CBA7' } : {}}
                                  title={timeStr || (isInvoiceMissing ? 'Facture manquante' : 'À préparer')}>{r}{timeStr ? ' ' + timeStr : ''}</span>;
                              })}
                            </div>
                          )}
                          {(s.order_refs || []).includes('MANUEL') && !(s.order_refs || []).some(r => r !== 'MANUEL' && !r.startsWith('[R')) && (
                            <div style={{ marginTop: 2 }}>
                              <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#EEF6FF', color: '#1A5276', border: '1px solid #B3D4F0' }}>✏️ Ajout manuel</span>
                            </div>
                          )}
                          {(s.order_refs || []).some(r => r.startsWith('[R')) && (
                            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                              {(s.order_refs || []).filter(r => r.startsWith('[R')).map(r => {
                                const orderRef = r.match(/\[R:([^\]]+)\]/)?.[1] || r.replace(/\[R[:\]]\s*/g, '');
                                const refDate = (s.order_refs_dates || {})[orderRef];
                                const dateStr = refDate ? (() => { try { const d = new Date(refDate.includes('T') ? refDate : refDate.replace(' ', 'T') + 'Z'); return isNaN(d) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); } catch { return ''; } })() : '';
                                return <span key={r} style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#FDEDEC', color: '#C0392B', border: '1px solid #F5B7B1' }}>⚠️ Reliquat {orderRef}{dateStr ? ' · ' + dateStr : ''}</span>;
                              })}
                            </div>
                          )}
                          {((s.nb_amb || 0) > 0 || (s.nb_froid || 0) > 0 || (s.nb_cartons || 0) > 0 || (s.nb_cartons_vrac || 0) > 0) && (
                            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                              {(s.nb_amb || 0) > 0 && <span className="badge badge-amb">{s.nb_amb} amb</span>}
                              {(s.nb_froid || 0) > 0 && <span className="badge badge-froid">{s.nb_froid} froid</span>}
                              {(s.nb_cartons || 0) + (s.nb_cartons_vrac || 0) > 0 && <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#D6EAF8', color: '#1A5276' }}>{(s.nb_cartons || 0) + (s.nb_cartons_vrac || 0)} carton{((s.nb_cartons || 0) + (s.nb_cartons_vrac || 0)) > 1 ? 's' : ''}</span>}
                            </div>
                          )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Résultats trouvés dans Départs */}
          {search && crossResults.length > 0 && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1A5276', marginBottom: 6, padding: '6px 10px', background: '#EEF6FF', borderRadius: 8 }}>
                📦 Trouvées dans Départs ({crossResults.length})
              </div>
              {crossResults.map(s => {
                const totalColis = (s.nb_amb || 0) + (s.nb_froid || 0) + (s.nb_cartons || 0) + (s.nb_cartons_vrac || 0);
                return (
                  <div key={s.id} style={{
                    padding: '10px 12px', borderRadius: 10, marginBottom: 4,
                    background: 'white', border: '1.5px solid #D6E4F0',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{s.partner_name}</div>
                        <div style={{ fontSize: 10, color: '#888' }}>{s.adresse || ''}</div>
                        <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: s.status === 'depart_valide' ? '#D6EAF8' : '#D5F5E3', color: s.status === 'depart_valide' ? '#1A5276' : '#1E6B3C' }}>
                            {s.status === 'depart_valide' ? '🚚 Partie' : '✅ Prête'}
                          </span>
                          {totalColis > 0 && <span style={{ fontSize: 9, color: '#888' }}>{totalColis} colis</span>}
                          {s.has_new_orders && <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#FDE68A', color: '#92400E' }}>⚠️ Nouvelle cmd</span>}
                        </div>
                      </div>
                      <button onClick={async () => {
                        if (!confirm('Remettre ' + s.partner_name + ' en préparation ?')) return;
                        await supabase.from('elixir_prep_sessions').update({
                          status: 'en_cours', validated_at: null, prepared_by: null,
                          updated_at: new Date().toISOString(),
                        }).eq('id', s.id);
                        handleSynced();
                      }} style={{
                        padding: '6px 12px', borderRadius: 8, border: '1.5px solid #D4680A',
                        background: '#FFF8F0', color: '#D4680A', fontSize: 11, fontWeight: 700,
                        cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
                      }}>↩️ En prépa</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && search && filtered.length === 0 && crossResults.length === 0 && sessions.length > 0 && (
            <div className="empty-state"><div className="empty-state-text">Aucune pharmacie pour « {search} »</div></div>
          )}
        </>}

        {tab === 'caisses' && <Lazy cle={module}><CaissesTab user={user} /></Lazy>}

        {/* ═══ ONGLET DÉPARTS (responsable) ═══ */}
        {tab === 'departs' && <Lazy cle={module}><DepartsTab sessions={sessions} onRefresh={handleSynced} user={user} /></Lazy>}

        {/* ═══ ONGLET ANOMALIES (responsable) ═══ */}
        {tab === 'anomalies' && <Lazy cle={module}><AnomaliesTab sessions={sessions} /></Lazy>}

        {/* ═══ ONGLET RELIQUATS (responsable) ═══ */}
        {tab === 'reliquats' && <Lazy cle={module}><ReliquatsTab onRefresh={async () => { await loadTodaySessions(); navigateToTab('pharma'); }} /></Lazy>}
      </div>
      </PullToRefresh>

      <OdooSync open={odooOpen} onClose={() => setOdooOpen(false)} onSynced={handleSynced} />

      {showAddPrepModal && (
        <AddPrepModal
          onClose={() => setShowAddPrepModal(false)}
          onAdded={() => { setShowAddPrepModal(false); loadTodaySessions(); }}
        />
      )}

      {/* Panneau rapide de préparation */}
      {quickPanelSession && !selectedSession && (
        <QuickPanel
          session={quickPanelSession}
          user={user}
          onClose={() => closeQuickPanel()}
          onSessionUpdate={(updated) => {
            if (updated._merged) {
              // Fusion : supprimer la session source, mettre à jour la cible
              setSessions(prev => {
                const without = prev.filter(s => s.id !== updated._deletedId);
                return without.map(s => s.id === updated.id ? { ...s, ...updated } : s);
              });
              setQuickPanelSession(null);
            } else {
              setQuickPanelSession(updated);
              setSessions(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
            }
          }}
          onOpenDetail={(s, section) => {
            setQuickPanelSession(null);
            openDetail(s, section);
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// AddPrepModal — Ajouter une pharmacie manuellement en préparation
// ═══════════════════════════════════════════════════════

function AddPrepModal({ onClose, onAdded }) {
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [manualName, setManualName] = useState('');
  const [tournee, setTournee] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase.from('elixir_prep_sessions')
        .select('partner_id, partner_name, adresse, tournee')
        .ilike('partner_name', '%' + search + '%')
        .order('date', { ascending: false })
        .limit(50);
      const seen = new Set();
      const unique = [];
      (data || []).forEach(s => {
        if (seen.has(s.partner_id)) return;
        seen.add(s.partner_id);
        unique.push(s);
      });
      setSuggestions(unique);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  function selectPharmacy(s) {
    setSelected(s);
    setSearch(s.partner_name);
    setTournee(s.tournee || '');
    setSuggestions([]);
  }

  async function handleAdd() {
    const name = selected?.partner_name || manualName.trim();
    if (!name) { alert('Nom de pharmacie requis'); return; }
    const partnerId = selected?.partner_id || Math.floor(Math.random() * -1000000);
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      // Vérifier si une session existe déjà pour cette pharmacie
      const { data: existing } = await supabase.from('elixir_prep_sessions')
        .select('id, status, partner_name, locked_at')
        .eq('partner_id', partnerId)
        .eq('date', today)
        .limit(5);

      // Vérifier si la pharmacie est verrouillée dans Départ (prete + locked = pas encore partie)
      const lockedSession = (existing || []).find(s => s.locked_at && s.status === 'prete');
      if (lockedSession) {
        alert(name + ' est verrouillée en Départ (en attente de départ). Déverrouille d\'abord dans l\'onglet Départs.');
        onClose();
        return;
      }

      if (existing && existing.length > 0) {
        const s = existing[0];
        if (s.status === 'facturee' || s.status === 'cloturee') {
          // Réactiver la session archivée — nettoyer les anciennes données
          await Promise.all([
            supabase.from('elixir_prep_photos').delete().eq('session_id', s.id),
            supabase.from('elixir_prep_caisses').delete().eq('session_id', s.id),
            supabase.from('elixir_prep_colis').delete().eq('session_id', s.id),
            supabase.from('elixir_prep_anomalies').delete().eq('session_id', s.id),
            supabase.from('elixir_prep_remarks').delete().eq('session_id', s.id),
          ]);
          // NOTE: on ne touche PAS caisse_stock ni mouvements
          await supabase.from('elixir_prep_sessions').update({
            status: 'en_cours',
            order_refs: ['MANUEL'],
            partner_name: name,
            adresse: selected?.adresse || '',
            tournee: tournee || '',
            prepared_by: null,
            nb_amb: 0, nb_froid: 0, nb_cartons: 0, nb_cartons_vrac: 0,
            asana_task_gid: null,
            date: today,
            updated_at: new Date().toISOString(),
          }).eq('id', s.id);
          onAdded();
          return;
        }
        // Session active existe déjà
        alert(name + ' est déjà dans les préparations (status: ' + s.status + ')');
        onClose();
        return;
      }

      // Pas de session existante → créer
      const { data, error } = await supabase.from('elixir_prep_sessions').insert({
        date: today,
        partner_id: partnerId,
        partner_name: name,
        adresse: selected?.adresse || '',
        tournee: tournee || '',
        order_refs: ['MANUEL'],
        order_refs_dates: {},
        invoice_refs: [],
        status: 'en_cours',
        nb_amb: 0, nb_froid: 0, nb_cartons: 0, nb_cartons_vrac: 0,
      }).select('id').single();
      if (error) throw error;
      if (!data) throw new Error('Aucune session créée');
      onAdded();
    } catch (e) {
      console.error('Erreur ajout manuel:', e);
      alert('Erreur: ' + e.message);
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 500,
        maxHeight: '80vh', overflow: 'auto', boxShadow: '0 -10px 40px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>

        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E8E8EE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>➕ Ajouter une préparation</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#888', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '16px 18px' }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555', display: 'block', marginBottom: 4 }}>Pharmacie</label>
            {!selected ? (
              <>
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setSelected(null); }}
                  placeholder="Rechercher une pharmacie..."
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #D6E4F0', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  autoFocus />
                {suggestions.length > 0 && (
                  <div style={{ border: '1px solid #E8E8EE', borderRadius: 8, marginTop: 4, maxHeight: 200, overflow: 'auto', background: 'white' }}>
                    {suggestions.map(s => (
                      <div key={s.partner_id} onClick={() => selectPharmacy(s)} style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', fontSize: 12,
                      }}>
                        <div style={{ fontWeight: 600 }}>{s.partner_name}</div>
                        <div style={{ color: '#888', fontSize: 10 }}>{s.adresse} {s.tournee ? '· T' + s.tournee : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
                {search.length >= 2 && suggestions.length === 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Pas trouvé ? Saisir le nom :</div>
                    <input type="text" value={manualName} onChange={e => setManualName(e.target.value)}
                      placeholder="Nom de la pharmacie"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #F5CBA7', fontSize: 14, fontFamily: 'inherit', background: '#FEF3CD', boxSizing: 'border-box' }} />
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#EEF6FF', borderRadius: 8, border: '1.5px solid #D6E4F0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{selected.partner_name}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{selected.adresse}</div>
                </div>
                <button onClick={() => { setSelected(null); setSearch(''); }} style={{ background: 'none', border: 'none', fontSize: 14, color: '#888', cursor: 'pointer' }}>✕</button>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555', display: 'block', marginBottom: 4 }}>Tournée</label>
            <select value={tournee} onChange={e => setTournee(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #D6E4F0', fontSize: 14, fontFamily: 'inherit', background: 'white', boxSizing: 'border-box' }}>
              <option value="">Sans tournée</option>
              <option value="IMPERATIFS">Impératifs</option>
              <option value="1">Tournée 1</option>
              <option value="2">Tournée 2</option>
              <option value="3">Tournée 3</option>
              <option value="4">Tournée 4</option>
              <option value="5">Tournée 5</option>
              <option value="6">Tournée 6</option>
              <option value="CHRONOPOST">Chronopost</option>
            </select>
          </div>
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #E8E8EE', display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px', borderRadius: 10, border: '1.5px solid #E2E2E8',
            background: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>Annuler</button>
          <button onClick={handleAdd} disabled={saving || (!selected && !manualName.trim())}
            style={{
              flex: 2, padding: '12px', borderRadius: 10, border: 'none',
              background: (selected || manualName.trim()) ? '#1A5276' : '#D1D5DB', color: 'white',
              fontSize: 13, fontWeight: 700, cursor: (selected || manualName.trim()) ? 'pointer' : 'default',
              fontFamily: 'inherit', opacity: saving ? 0.6 : 1,
            }}>
            {saving ? 'Ajout…' : 'Ajouter en préparation'}
          </button>
        </div>
      </div>
    </div>
  );
}
